import { isApiFailure } from '@agion/ui/api-failure'

/**
 * The server's "saved, access not confirmed yet" answer to a mission write
 * (agion/Agion#2677): HTTP 503 whose body says
 * `error: "mission_authorization_sync_pending"` and `mutation_persisted: true`.
 * The write is stored; `doc_version` and `version_number` are the versions it
 * stored. The server owns this verdict: the browser keys only on those two
 * fields and never decides it from anything else. Any other 503 keeps its
 * own (transient) handling.
 */
export const MISSION_AUTHORIZATION_SYNC_PENDING = 'mission_authorization_sync_pending'

/**
 * English text of the note every design-writing surface shows for it, under
 * the one key pages:missionDesigner.permissionPending (written literally at
 * each t() call so the i18n key check sees it).
 */
export const PERMISSION_PENDING_DEFAULT =
  'Saved. Access for this change is not confirmed yet; your next edit or Save retries it.'

export interface MissionWritePermissionPending {
  /** The document version the write stored, when the server sent it. */
  docVersion?: number
  /** The row version the write stored, when the server sent it. */
  versionNumber?: number
}

function version(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined
}

/** The stored versions when error is the server's pending answer, otherwise null. */
export function missionWritePermissionPending(error: unknown, missionId?: string): MissionWritePermissionPending | null {
  if (!isApiFailure(error) || error.status !== 503) return null
  const data = error.data
  if (typeof data !== 'object' || data === null) return null
  const body = data as Record<string, unknown>
  if (body['error'] !== MISSION_AUTHORIZATION_SYNC_PENDING || body['mutation_persisted'] !== true) return null
  // Removals require the matching mission receipt, never a preview verdict.
  if (missionId !== undefined && (body['mission_id'] !== missionId || (body['preview'] !== undefined && body['preview'] !== false))) return null
  return { docVersion: version(body['doc_version']), versionNumber: version(body['version_number']) }
}

/**
 * Settles a mission write that the server stored with its access pending:
 * the write counts as saved and readStored supplies what was stored, since the
 * pending answer carries no mission. Any other failure is rethrown unchanged.
 */
export async function savedOrPermissionPending<T>(
  write: Promise<T>,
  readStored: () => Promise<T>
): Promise<{ value: T; permissionPending: boolean }> {
  try {
    return { value: await write, permissionPending: false }
  } catch (error) {
    if (!missionWritePermissionPending(error)) throw error
    return { value: await readStored(), permissionPending: true }
  }
}
